// Opening the app from a notification (issue #21).
//
// The service worker used to call WindowClient.navigate() on an open window.
// On an iPhone Home Screen app that is suspended in the background, that
// leaves a white page until the app is force-closed, and a full reload also
// throws away the app's own history, so Back misbehaved. Now the worker
// focuses the window and posts NOTIFICATION_OPEN; the app routes in place.

export const NOTIFICATION_OPEN = 'notification:open';
/**
 * The chat a NOTIFICATION_OPEN message opens: its id, null for a
 * notification that opens no chat, undefined for any other message.
 */
export function notificationConversationId(
  data: unknown,
  origin: string
): string | null | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const { type, url, conversationId } = data as {
    type?: unknown;
    url?: unknown;
    conversationId?: unknown;
  };
  if (type !== NOTIFICATION_OPEN) return undefined;
  if (typeof conversationId === 'string' && conversationId) {
    return conversationId;
  }
  if (typeof url !== 'string') return null;
  try {
    const target = new URL(url, origin);
    if (target.origin !== origin) return null;
    return target.searchParams.get('conversation') || null;
  } catch {
    return null;
  }
}

/** `/?conversation=<id>&call=<callId>`: the push for an incoming call. */
export const CALL_PARAM = 'call';

interface Place {
  pathname: string;
  search: string;
  hash: string;
}

export interface NotificationNavigation {
  to: string;
  /** The link is the screen already showing (give or take the call id). */
  replace: boolean;
}

/** Where a service worker message asks the app to go, or null. */
export function notificationNavigation(
  data: unknown,
  origin: string,
  current: Place
): NotificationNavigation | null {
  if (!data || typeof data !== 'object') return null;
  const { type, url } = data as { type?: unknown; url?: unknown };
  if (type !== NOTIFICATION_OPEN || typeof url !== 'string') return null;

  let target: URL;
  try {
    target = new URL(url, origin);
  } catch {
    return null;
  }
  if (target.origin !== origin) return null;

  const to = `${target.pathname}${target.search}${target.hash}`;
  // The call id is dropped as soon as it is read, so compare without it; a
  // second entry for the same chat would make Back seem to do nothing.
  const same =
    target.pathname === current.pathname &&
    withoutCallParam(target.search).search ===
      withoutCallParam(current.search).search &&
    target.hash === current.hash;
  return { to, replace: same };
}

type Navigate = (
  to: string,
  options: { replace: boolean; state: unknown }
) => void;

/**
 * Route NOTIFICATION_OPEN messages from the service worker with the app's
 * router. `here().state` is the current entry's router state. Returns a
 * function that stops listening.
 */
export function listenForNotificationOpens(
  container: Pick<EventTarget, 'addEventListener' | 'removeEventListener'>,
  navigate: Navigate,
  here: () => Place & { origin: string; state: unknown }
): () => void {
  const onMessage = (event: Event) => {
    const current = here();
    const next = notificationNavigation(
      (event as MessageEvent).data,
      current.origin,
      current
    );
    if (!next) return;
    // A new entry is marked as opened from where we were, so the chat's Back
    // pops it and returns there. Replacing keeps the entry's own state: a
    // chat reached by deep link has nothing in the app behind it, and
    // marking it would send Back out of the app's history.
    navigate(next.to, {
      replace: next.replace,
      state: next.replace ? current.state : { openedFromList: true },
    });
  };
  container.addEventListener('message', onMessage);
  return () => container.removeEventListener('message', onMessage);
}

/** The call id in a query string, and the query string without it. */
export function withoutCallParam(search: string): {
  callId: string | null;
  search: string;
} {
  const params = new URLSearchParams(search);
  const callId = params.get(CALL_PARAM);
  params.delete(CALL_PARAM);
  const rest = params.toString();
  return { callId, search: rest ? `?${rest}` : '' };
}

export interface ReconnectSession {
  callId: string;
  direction: 'incoming' | 'outgoing';
  /** Answered on this device (we have our camera/mic). */
  answered: boolean;
  /** The server has told us we're in the call (our own call:accept). */
  confirmed: boolean;
}

/**
 * What to do with call state once the socket signs in again. The server lists
 * the calls still ringing for us (and re-sends their call:initiate).
 *
 * - `rejoin`: we're in the call; ask to be re-announced (call:rejoin).
 * - `accept`: we answered while the socket was down, so the answer never
 *   left; the server still rings us, so send it again (issue #21).
 * - `drop`: a ring we hadn't answered has stopped.
 * - `missed`: the call whose notification was tapped has stopped ringing,
 *   and it wasn't answered or declined on this device.
 */
export function afterReconnect(input: {
  session: ReconnectSession | null;
  /** A call whose notification was tapped. */
  expectedCallId: string | null;
  /** Calls answered or declined on this device. */
  handledCallIds: ReadonlySet<string>;
  ringingCallIds: string[] | undefined;
}): { action: 'none' | 'rejoin' | 'accept' | 'drop'; missed: boolean } {
  const ringing = input.ringingCallIds ?? [];
  const { session, expectedCallId } = input;
  const missed =
    expectedCallId !== null &&
    !ringing.includes(expectedCallId) &&
    !input.handledCallIds.has(expectedCallId);

  if (!session) return { action: 'none', missed };
  const stillRinging = ringing.includes(session.callId);
  let action: 'none' | 'rejoin' | 'accept' | 'drop';
  if (session.direction === 'outgoing') {
    // Before our camera/mic arrive, call:initiate hasn't been sent: the
    // server doesn't know the call yet, and startCall sends it shortly.
    action = session.answered ? 'rejoin' : 'none';
  } else if (session.confirmed) {
    action = 'rejoin';
  } else if (session.answered) {
    // Not ringing any more: the answer may have got through even though
    // its confirmation didn't; rejoin, and the server says if it's gone.
    action = stillRinging ? 'accept' : 'rejoin';
  } else {
    action = stillRinging ? 'none' : 'drop';
  }
  return { action, missed };
}
